import { Calendar, Clock, Copy, Edit, Globe, MoreHorizontal, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Can } from "@/lib/authorization";
import { useHolidayUIStore } from "../store/use-holiday-ui-store";
import type { Holiday } from "../types";
import { HolidayScope } from "../types";
import { formatHolidayDateRange } from "../utils/format-holiday-date-range";

interface HolidayCardProps {
  holiday: Holiday & {
    startDateTimeLocal?: any;
    endDateTimeLocal?: any;
  };
}

export function HolidayCard({ holiday }: HolidayCardProps) {
  const { t } = useTranslation("settings");
  const {
    openEditDialog,
    openDuplicateDialog,
    openDeleteDialog,
    selectedHolidayIds,
    toggleHolidaySelection,
  } = useHolidayUIStore();
  const dateRange = formatHolidayDateRange(holiday.startDateTime, holiday.endDateTime);

  // Get scope display
  const scopeDisplay = holiday.scope === HolidayScope.FRANCHISE_WIDE ? "All Salons" : "This Salon";
  const scopeVariant = holiday.scope === HolidayScope.FRANCHISE_WIDE ? "default" : "secondary";

  // Get affected salons for display
  const affectedSalons = holiday.holidaySalons?.map((hs) => hs.salon?.name).filter(Boolean) || [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <Can I="delete" a="Holiday">
                <Checkbox
                  checked={selectedHolidayIds.includes(holiday.id)}
                  onCheckedChange={() => toggleHolidaySelection(holiday.id)}
                  aria-label={t("holidays.bulk.selectHoliday", { name: holiday.name })}
                />
              </Can>
              <h3 className="font-semibold text-lg">{holiday.name}</h3>
            </div>
            {holiday.message && <p className="text-sm text-muted-foreground">{holiday.message}</p>}
          </div>

          <div className="flex items-center gap-2">
            <Badge variant={scopeVariant}>
              <Globe className="w-3 h-3 mr-1" />
              {scopeDisplay}
            </Badge>

            {holiday.blockOnlineBooking && (
              <Badge variant="destructive">{t("labels.blockOnlineBooking")}</Badge>
            )}

            <Can I="update" a="Holiday" passThrough>
              {(canUpdate) =>
                canUpdate && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditDialog(holiday.id)}>
                        <Edit className="w-4 h-4 mr-2" />
                        {t("holidays.editHoliday")}
                      </DropdownMenuItem>
                      <Can I="create" a="Holiday">
                        <DropdownMenuItem onClick={() => openDuplicateDialog(holiday.id)}>
                          <Copy className="w-4 h-4 mr-2" />
                          {t("holidays.duplicateHoliday")}
                        </DropdownMenuItem>
                      </Can>
                      <Can I="delete" a="Holiday">
                        <DropdownMenuItem
                          onClick={() => openDeleteDialog(holiday.id)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          {t("holidays.deleteHoliday")}
                        </DropdownMenuItem>
                      </Can>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )
              }
            </Can>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="w-4 h-4" />
            <span>{dateRange}</span>
          </div>

          {holiday.scope === HolidayScope.SALON_SPECIFIC && affectedSalons.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="w-4 h-4" />
              <span>Affects: {affectedSalons.join(", ")}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
